<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[10050] bg-black text-white">
      <video
        v-show="!previewUrl"
        ref="videoRef"
        class="absolute inset-0 h-full w-full object-cover"
        autoplay
        playsinline
        muted
      />
      <video
        v-if="previewUrl"
        class="absolute inset-0 h-full w-full object-cover"
        :src="previewUrl"
        controls
      />
      <div :class="railClass">
        <div :class="controlsClass">
          <button
            type="button"
            class="justify-self-start"
            :class="cameraButton()"
            :aria-label="t('cancel')"
            @click="handleClose"
          >
            <XIcon class="size-6" />
          </button>
          <button
            v-if="!previewUrl"
            type="button"
            class="justify-self-center"
            :class="cameraButton({ variant: 'shutter' })"
            :aria-label="isRecording ? t('stop') : t('record')"
            @click="isRecording ? handleStop() : handleStart()"
          >
            <span
              class="absolute bg-red-500 transition-all"
              :class="isRecording ? 'inset-[1.5rem] rounded-md' : 'inset-[6px] rounded-full'"
            />
          </button>
          <button
            v-else
            type="button"
            class="justify-self-center"
            :class="cameraButton({ variant: 'confirm' })"
            :aria-label="t('confirm')"
            @click="handleUse"
          >
            <CheckIcon class="size-[2.5rem]" />
          </button>
          <button
            v-if="previewUrl"
            type="button"
            class="justify-self-end"
            :class="cameraButton()"
            :aria-label="t('retake')"
            @click="handleRetake"
          >
            <RotateCcwIcon class="size-6" />
          </button>
          <button
            v-else-if="!isRecording"
            type="button"
            class="justify-self-end"
            :class="cameraButton()"
            :aria-label="t('skip')"
            @click="handleSkip"
          >
            <Redo2Icon class="size-6" />
          </button>
          <span v-else class="block size-[3.25rem] justify-self-end" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onUnmounted, ref } from 'vue';
import { cameraButton } from '@carvia/theme/lib/cva/camera-button';
import { useI18n } from 'vue-i18n';
import { CheckIcon, Redo2Icon, RotateCcwIcon, XIcon } from 'lucide-vue-next';
import posthog from 'posthog-js';
import { useCameraStream } from '@/hooks/use-camera-stream';
import { useCameraLayout } from '@/hooks/use-camera-layout';
import { DAMAGE_VIDEO_BITRATE_FLAG, getVideoBitsPerSecond } from '@/utils/damage-evidence';

const emit = defineEmits<{
  (e: 'recorded', file: File): void;
  (e: 'skip'): void;
  (e: 'close'): void;
  (e: 'failed'): void;
}>();

const { t } = useI18n();
const { railClass, controlsClass } = useCameraLayout();
const videoRef = ref<HTMLVideoElement | null>(null);
const { stream } = useCameraStream(
  videoRef,
  { audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } } },
  () => emit('failed'),
);
const recorder = ref<MediaRecorder | null>(null);
const chunks = ref<Blob[]>([]);
const isRecording = ref(false);
const previewUrl = ref('');
const recordedFile = ref<File | null>(null);

/** Preferred recording formats; iPad and iPhone only record MP4. Damage videos have no sound. */
const RECORDING_TYPES = [
  'video/webm;codecs=vp9',
  'video/webm',
  'video/mp4;codecs=avc1',
  'video/mp4',
];

const handleStart = () => {
  if (!stream.value) return;
  chunks.value = [];
  const mimeType = RECORDING_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
  recorder.value = new MediaRecorder(stream.value, {
    mimeType,
    videoBitsPerSecond: getVideoBitsPerSecond(posthog.getFeatureFlag(DAMAGE_VIDEO_BITRATE_FLAG)),
  });
  recorder.value.ondataavailable = (event) => {
    if (event.data.size) chunks.value.push(event.data);
  };
  recorder.value.onstop = () => {
    // Safari records MP4 whatever was asked for; name the file after what was actually recorded.
    const type = recorder.value?.mimeType || mimeType || 'video/webm';
    const extension = type.startsWith('video/mp4') ? 'mp4' : 'webm';
    const blob = new Blob(chunks.value, { type });
    recordedFile.value = new File([blob], `damage-video.${extension}`, { type });
    if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = URL.createObjectURL(blob);
  };
  recorder.value.start();
  isRecording.value = true;
};

const handleStop = () => {
  recorder.value?.stop();
  isRecording.value = false;
};

const handleRetake = () => {
  recordedFile.value = null;
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = '';
};

const handleUse = () => {
  if (recordedFile.value) emit('recorded', recordedFile.value);
};

const handleSkip = () => {
  emit('skip');
};

/** Stops a running recording without building a preview for it. */
const discardRecording = () => {
  if (!recorder.value || !isRecording.value) return;
  recorder.value.onstop = null;
  recorder.value.stop();
  isRecording.value = false;
};

const handleClose = () => {
  discardRecording();
  emit('close');
};

onUnmounted(() => {
  discardRecording();
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
});
</script>

<i18n lang="json">
{
  "en": {
    "record": "Record",
    "stop": "Stop",
    "retake": "Retake",
    "confirm": "Confirm",
    "skip": "Skip",
    "cancel": "Cancel"
  },
  "de": {
    "record": "Aufnehmen",
    "stop": "Stopp",
    "retake": "Erneut",
    "confirm": "Bestätigen",
    "skip": "Überspringen",
    "cancel": "Abbrechen"
  },
  "es": {
    "record": "Grabar",
    "stop": "Parar",
    "retake": "Repetir",
    "confirm": "Confirmar",
    "skip": "Omitir",
    "cancel": "Cancelar"
  }
}
</i18n>
